import { PtwError } from '@/lib/commands'
import { getSnapshot, requireActiveClient } from '@/lib/session'

function changedIdentity(): PtwError {
  return new PtwError('PW004', 'LEARNING_IDENTITY_CHANGED', {
    problem: 'Your sign-in changed. Reopen Help & Learning under your own account before recording progress.',
  })
}

/**
 * Keep a learning request with the person who opened it. Account builds share one
 * Supabase client: resolving its token later must not give a pending assessment,
 * practical or practice save to the next person signing in on this browser.
 * The server still derives the learner/assessor from the token, never these args.
 */
export async function callLearningRpc<T>(identityKey: string, name: string, args: Record<string, unknown> = {}): Promise<T> {
  const origin = getSnapshot()
  if (!origin.identityKey || origin.identityKey !== identityKey) throw changedIdentity()
  const client = requireActiveClient()
  const { data: auth, error: authError } = await client.auth.getSession()
  if (authError) throw PtwError.from(authError)
  const session = auth.session
  if (!session?.access_token) throw changedIdentity()
  if (origin.accountMode && (identityKey !== `user:${session.user.id}` || session.user.is_anonymous === true)) {
    throw changedIdentity()
  }
  if (getSnapshot().identityKey !== identityKey || requireActiveClient() !== client) throw changedIdentity()

  // Per-request headers only. The SDK may await another token lookup internally;
  // this explicit header retains the originating token without changing the client.
  const { data, error } = await client.rpc(name, args).setHeader('Authorization', `Bearer ${session.access_token}`)
  if (error) throw PtwError.from(error)
  return data as T
}
